import { formatShortDateOrDash } from "@shared-core/domain/formatters";
import type {
  DeliveryConsignment,
  JobDeliveryView,
} from "@shared-core/data/prepress-production-types";

/**
 * A job's planned delivery, as Tharstern holds it: the job-header method and
 * mailing summary plus every planned consignment (a split / multi-drop
 * delivery is several). Read-only; the job workspace's Delivery section.
 */
export function JobDeliveryBody({
  delivery,
}: {
  delivery: JobDeliveryView | null;
}) {
  if (!delivery || delivery.consignments.length === 0) {
    // A job has a destination from the moment it is raised — it is on the job
    // header as a delivery code. What does not exist yet is the consignment;
    // despatch creates that. Reading consignments alone made every draft job
    // claim it had no delivery, which was never true, so the resolved header
    // address is shown when there is one and the empty state is reserved for
    // a job that genuinely has no destination set.
    const primary = delivery?.primaryDelivery ?? null;
    return (
      <div className="space-y-3">
        {delivery && (delivery.despatchMethod || delivery.mailingComponent) ? (
          <DeliveryHeaderSummary delivery={delivery} />
        ) : null}
        {primary ? (
          <>
            <FieldLabel>Delivery destination</FieldLabel>
            <PrimaryDeliveryCard primary={primary} />
            <p className="text-xs text-muted-foreground">
              Taken from the job in Tharstern. A consignment appears here once
              despatch raises the delivery note.
            </p>
          </>
        ) : delivery?.primaryDeliveryCode ? (
          // The job DOES have a destination; the address book row behind the
          // code is missing or deleted, so the backend deliberately sends the
          // code without an address rather than fabricating a blank one (see
          // `PrimaryDeliveryOut`). Saying "no planned delivery" here would be
          // the exact false claim this step was fixed to stop making.
          <div className="rounded-md border border-dashed border-border p-4 text-sm">
            <span className="font-medium">
              Delivering to {delivery.primaryDeliveryCode}
            </span>
            <p className="mt-1 text-xs text-muted-foreground">
              The job carries this destination, but no address book entry
              matches the code. Check it in Tharstern before despatch.
            </p>
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
            No planned delivery on this job yet. Consignments appear here once
            the delivery is set in Tharstern.
          </div>
        )}
      </div>
    );
  }
  const multi = delivery.consignments.length > 1;
  return (
    <div className="space-y-3">
      <DeliveryHeaderSummary delivery={delivery} />
      <div className="flex items-center justify-between">
        <FieldLabel>
          {multi
            ? `${delivery.consignments.length} delivery destinations`
            : "Delivery destination"}
        </FieldLabel>
        {multi && (
          <span className="text-[10px] uppercase tracking-wider text-amber-700 font-medium">
            Multi-drop
          </span>
        )}
      </div>
      <div className="space-y-2">
        {delivery.consignments.map((c) => (
          <ConsignmentCard key={c.deliveryNoteId} consignment={c} />
        ))}
      </div>
    </div>
  );
}

function DeliveryHeaderSummary({ delivery }: { delivery: JobDeliveryView }) {
  const bits: { label: string; value: string }[] = [];
  if (delivery.despatchMethod)
    bits.push({ label: "Method", value: delivery.despatchMethod });
  if (delivery.mailingComponent)
    bits.push({ label: "Mailing", value: delivery.mailingComponent });
  if (delivery.primaryDeliveryCode)
    bits.push({ label: "Account", value: delivery.primaryDeliveryCode });
  if (bits.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
      {bits.map((b) => (
        <span key={b.label}>
          <span className="text-muted-foreground">{b.label}: </span>
          <span className="font-medium">{b.value}</span>
        </span>
      ))}
    </div>
  );
}

/** The job header's destination, resolved to an address. Visually quieter
 *  than a ConsignmentCard because it is a plan, not a packed consignment —
 *  there is no weight, no box count and no ship-by date to show. */
function PrimaryDeliveryCard({
  primary,
}: {
  primary: NonNullable<JobDeliveryView["primaryDelivery"]>;
}) {
  const addressLines = [primary.address, primary.postcode, primary.country]
    .map((l) => l?.trim())
    .filter((l): l is string => Boolean(l));
  const courierBits = [
    primary.courier,
    primary.courierServiceId ? `service ${primary.courierServiceId}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="rounded-md border border-border bg-background p-3 text-sm">
      <div className="font-semibold">
        {primary.name || primary.deliveryCode}
      </div>
      {addressLines.length > 0 && (
        <div className="text-muted-foreground">{addressLines.join(", ")}</div>
      )}
      <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
        <span>Account: {primary.deliveryCode}</span>
        {primary.contact && <span>Contact: {primary.contact}</span>}
        {courierBits && <span>{courierBits}</span>}
      </div>
    </div>
  );
}

function ConsignmentCard({
  consignment: c,
}: {
  consignment: DeliveryConsignment;
}) {
  const addressLines = [c.address, c.town, c.county, c.postcode, c.country]
    .map((l) => l?.trim())
    .filter((l): l is string => Boolean(l));
  const courierBits = [
    c.courier,
    c.courierServiceId ? `service ${c.courierServiceId}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const logistics = [
    c.boxes ? `${c.boxes} box${c.boxes > 1 ? "es" : ""}` : null,
    c.pallets ? `${c.pallets} pallet${c.pallets > 1 ? "s" : ""}` : null,
    c.weight ? `${c.weight} kg` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="rounded-md border border-border bg-background p-3 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold truncate">
            {c.name || c.deliveryCode || "Delivery address"}
          </div>
          {addressLines.length > 0 && (
            <div className="text-muted-foreground">
              {addressLines.join(", ")}
            </div>
          )}
          {c.contact && (
            <div className="text-xs text-muted-foreground mt-0.5">
              Contact: {c.contact}
              {c.tel ? ` · ${c.tel}` : ""}
            </div>
          )}
        </div>
        {c.reqDate && (
          <div className="text-xs text-muted-foreground whitespace-nowrap">
            Required {formatShortDateOrDash(c.reqDate)}
          </div>
        )}
      </div>
      {(courierBits || logistics) && (
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground border-t border-dashed border-border pt-2">
          {courierBits && <span>{courierBits}</span>}
          {logistics && <span>{logistics}</span>}
          {c.deliveryCode ? (
            <span>Book ref {c.deliveryCode}</span>
          ) : (
            <span className="italic">Ad-hoc address</span>
          )}
        </div>
      )}
      {c.instructions && (
        <div className="mt-2 text-xs text-foreground">
          <span className="text-muted-foreground">Instructions: </span>
          {c.instructions}
        </div>
      )}
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
      {children}
    </div>
  );
}
